import {
  SYSTEM_MANAGED_DEVICE_PARAMS,
  SYSTEM_MANAGED_NETWORK_PARAMS,
} from "constant/application";
import { INTEGRATION_TYPES } from "constant/device";
import { DEVICE_SHELL } from "constant/device-status";
import find from "lodash/find";
import get from "lodash/get";
import * as DS from "redux-store/device-statuses/selectors";
import { reduceById } from "redux-store/reducers";
import { defaultObject } from "redux-store/selectors";
import { createSelector } from "reselect";
import semverGt from "semver/functions/gt";
import semverGte from "semver/functions/gte";
import semverNeq from "semver/functions/neq";
import semverValid from "semver/functions/valid";
import { sortByLabelAsc } from "util/sort-by-prop";

function findbyDeviceId(byId, deviceId) {
  return Object.values(byId).find((d) => d.device_id === deviceId);
}

function _deviceIdSelector(state, deviceId) {
  return deviceId;
}

const byDeviceIdSelector = createSelector(
  devicesSelector,
  _deviceIdSelector,
  findbyDeviceId,
);

function getByDeviceId(state, deviceId) {
  return find(state, { device_id: deviceId });
}

function devicesSelector(state) {
  return get(state, "global.devices.byId", defaultObject);
}

function getDeviceType(device) {
  const { current_version: version, is_agent: isAgent } = device;
  let deviceType = "No Info";

  if (isAgent) {
    deviceType = "Agent";
  } else if (version) {
    deviceType = "Player";
  }

  return deviceType;
}

// TODO(etsai): cor-3297: We should retrieve integration type from device model
//  once we implement subclass for Device, UI is not responsible for this.
function getDeviceIntegrationType(device) {
  const { is_agent: isAgent, is_thirdparty: isThirdParty } = device;

  if (isAgent) {
    return INTEGRATION_TYPES.AGENT;
  } else if (isThirdParty) {
    return INTEGRATION_TYPES.THIRDPARTY;
  }

  return INTEGRATION_TYPES.PLAYER;
}

function getSystemManagedParams(device) {
  if (!isSystemManaged(device)) {
    return null;
  }

  const type = getDeviceIntegrationType(device);

  return [
    // Device param has higher priority than network's, we can't allow users to
    // edit synced network params at device level.
    ...SYSTEM_MANAGED_NETWORK_PARAMS,
    ...SYSTEM_MANAGED_DEVICE_PARAMS[type],
  ];
}

function getName(device) {
  return device.name ? device.name : device.device_id;
}

function getNetworkChangeDate(device) {
  return device.network_change_date
    ? device.network_change_date
    : device.registration_date;
}

function hasPoint(device) {
  const { location } = device;

  if (!location) {
    return false;
  }

  const latlng = location.split(",");

  return latlng.length === 2;
}

function getPoint(device) {
  if (!hasPoint(device)) {
    return null;
  }

  const { location } = device;
  const latlng = location.split(",");

  return {
    lat: Number(latlng[0]),
    lng: Number(latlng[1]),
  };
}

function hasConnected(device = {}) {
  return device.has_connected || false;
}

function hasLatestFirmware(device = {}) {
  const { latest_firmware: latestFirmware, current_firmware: currentFirmware } =
    device;

  if (!latestFirmware) {
    return true;
  }

  if (!currentFirmware) {
    return true;
  }

  return latestFirmware === currentFirmware;
}

function getStatus(device) {
  const { status = {} } = device;

  if (!hasConnected(device)) {
    return DEVICE_SHELL;
  }

  return DS.getStatus(status);
}

function isOnline(device) {
  const { status = {} } = device;

  return DS.isOnline(status);
}

function isHibernating(device) {
  const { status = {} } = device;

  return DS.isHibernating(status);
}

function isSystemManaged(device = {}) {
  return device.system_managed || false;
}

function isTesting(device) {
  return device.testing;
}

function needsFirmwareUpgrade(device) {
  return device.needs_firmware_upgrade || false;
}

function needsSoftwareUpgrade(device) {
  return device.needs_upgrade || false;
}

function needsSoftwareDowngrade(device, player_version_downgrade_limit) {
  if (
    device.can_downgrade &&
    device.current_version &&
    player_version_downgrade_limit
  ) {
    return semverGte(device.current_version, player_version_downgrade_limit);
  } else {
    return false;
  }
}

function needsSoftwareUpgradeV2(
  device,
  target_version = null,
  onlyAgent = false,
  onlyWebos = false,
) {
  const latestVersion = semverValid(device.latest_version);
  const targetVersion = semverValid(target_version) || latestVersion;
  const currentVersion = semverValid(device.current_version);
  const networkVersion = semverValid(device.network_version);
  const pinned_mismatch =
    networkVersion && targetVersion && semverNeq(networkVersion, targetVersion);
  const is_webos = device.os_platform === "webos";

  return (
    currentVersion &&
    targetVersion &&
    device.is_agent === onlyAgent &&
    is_webos === onlyWebos &&
    !pinned_mismatch &&
    !device.device_update_disabled &&
    semverGt(targetVersion, currentVersion)
  );
}

function needsSoftwareDowngradeV2(device, min_current_version, target_version) {
  const targetVersion = semverValid(target_version);
  const currentVersion = semverValid(device.current_version);
  const minCurrentVersion = semverValid(min_current_version);
  const networkVersion = semverValid(device.network_version);
  const pinned_mismatch =
    networkVersion && targetVersion && semverNeq(networkVersion, targetVersion);

  return (
    currentVersion &&
    targetVersion &&
    minCurrentVersion &&
    !device.is_agent &&
    !pinned_mismatch &&
    !device.device_update_disabled &&
    semverGte(currentVersion, minCurrentVersion) &&
    semverGt(currentVersion, targetVersion)
  );
}

function canCancelUpgrade(device) {
  const targetVersion = semverValid(device.target_version);
  const currentVersion = semverValid(device.current_version);

  return (currentVersion && targetVersion && semverNeq(currentVersion, targetVersion));
}

function generateTargetOption(target) {
  switch (target.type) {
    case "d":
      return generateDeviceOption(target);
    case "t":
      return generateTagOption(target);
    default:
      break;
  }
}

function generateDeviceOption(device) {
  return {
    label: `device: ${device.name} (${device.device_id})`,
    value: `d_${device.id}`,
  };
}

function generateTagOption(tag) {
  return {
    label: `tag: ${tag.name}`,
    value: `t_${tag.id}`,
  };
}

function getTargets(devices, includeSystemTags = false) {
  if (!devices.length) {
    return [];
  }

  const tagIds = new Set();
  const targets = [];

  devices.forEach((device) => {
    if (device.deleted) {
      return;
    }

    targets.push(generateDeviceOption(device));

    device.tags.forEach((tag) => {
      const { id, name } = tag;
      if (tagIds.has(id)) {
        return;
      }

      if (includeSystemTags || !name.startsWith("_")) {
        tagIds.add(id);
        targets.push(generateTagOption(tag));
      }
    });
  });

  return targets.sort(sortByLabelAsc);
}

function getAllDevicesTags(devices, includeSystemTags = false) {
  const byId = reduceById({}, devices);

  return getTagsWithIds(byId, includeSystemTags);
}

function getDeviceTags(device) {
  const byId = reduceById({}, [device]);

  return getTagsWithIds(byId, false);
}

function getTagsWithIds(devices, includeSystemTags = false) {
  const tagIds = new Set();
  const deviceTags = [];

  Object.values(devices).forEach((device) => {
    device.tags.forEach((tag) => {
      const { id, name } = tag;
      if (tagIds.has(id)) {
        return;
      }

      if (includeSystemTags || !name || name[0] !== "_") {
        tagIds.add(id);
        deviceTags.push({
          label: name,
          value: id,
        });
      }
    });
  });

  return deviceTags.sort(sortByLabelAsc);
}

function getTagFromDevices(devices, tagId) {
  for (const d of devices) {
    const tag = d.tags.find((t) => t.id === Number(tagId));
    if (tag) {
      return tag;
    }
  }
  return null;
}

export {
  byDeviceIdSelector,
  devicesSelector,
  findbyDeviceId,
  generateDeviceOption,
  generateTargetOption,
  generateTagOption,
  getAllDevicesTags,
  getByDeviceId,
  getDeviceIntegrationType,
  getDeviceTags,
  getDeviceType,
  getName,
  getNetworkChangeDate,
  getPoint,
  getStatus,
  getSystemManagedParams,
  getTagFromDevices,
  getTagsWithIds,
  getTargets,
  hasConnected,
  hasLatestFirmware,
  hasPoint,
  isHibernating,
  isOnline,
  isSystemManaged,
  isTesting,
  needsFirmwareUpgrade,
  needsSoftwareUpgrade,
  needsSoftwareDowngrade,
  needsSoftwareUpgradeV2,
  needsSoftwareDowngradeV2,
  canCancelUpgrade,
};
